import { StandardEvents } from '@shopify/events';

/**
 * Closes atelier ("Créneau") variant options that start in less than this
 * many hours, in addition to the theme's native sold-out handling.
 */
const HOURS_BEFORE_CLOSE = 48;

/**
 * @param {string | undefined} startValue - ISO 8601 datetime string from the
 *   variant metafield `ateliers.debut`.
 * @returns {boolean}
 */
function isSlotClosed(startValue) {
  if (!startValue) return false;
  const start = new Date(startValue);
  if (Number.isNaN(start.getTime())) return false;
  const hoursUntilStart = (start.getTime() - Date.now()) / 3600000;
  return hoursUntilStart < HOURS_BEFORE_CLOSE;
}

/**
 * @param {HTMLInputElement} input
 * @returns {boolean} whether this slot is closed
 */
function applySlotState(input) {
  const closed = isSlotClosed(input.dataset.slotStart);
  const label = input.closest('.variant-option__button-label');
  const closedMessage = label?.querySelector('[data-key="atelier-slot-closed"]');
  const spots = label?.querySelector('[data-key="atelier-slot-spots"]');

  if (closed) {
    if (input.disabled !== true) input.disabled = true;
    if (input.dataset.optionAvailable !== 'false') input.dataset.optionAvailable = 'false';
    if (input.getAttribute('aria-disabled') !== 'true') input.setAttribute('aria-disabled', 'true');
    if (closedMessage && closedMessage.hidden !== false) closedMessage.hidden = false;
    if (spots && spots.hidden !== true) spots.hidden = true;
  } else if (input.dataset.atelierSoldOut !== 'true') {
    // Only re-enable if the slot isn't also sold out server-side.
    if (input.disabled !== false) input.disabled = false;
    if (input.dataset.optionAvailable === 'false') input.dataset.optionAvailable = 'true';
    if (closedMessage && closedMessage.hidden !== true) closedMessage.hidden = true;
    if (spots && spots.hidden !== false) spots.hidden = false;
  }

  return closed;
}

/**
 * @param {Element} picker - a <variant-picker> element.
 */
function refreshVariantPicker(picker) {
  const inputs = Array.from(picker.querySelectorAll('input[data-atelier-slot="true"]'));
  if (!inputs.length) return;

  // Remember which inputs are server-flagged as sold out *before* we touch
  // anything, so our own edits are never mistaken for the server's state.
  inputs.forEach((input) => {
    if (input.dataset.atelierSoldOut === undefined) {
      input.dataset.atelierSoldOut = input.dataset.optionAvailable === 'false' ? 'true' : 'false';
    }
  });

  let checkedIsClosed = false;
  let firstValid;

  inputs.forEach((input) => {
    const closed = applySlotState(input);
    const isChecked = input.checked || input.dataset.currentChecked === 'true';
    if (isChecked && closed) checkedIsClosed = true;
    if (!firstValid && !closed && input.dataset.atelierSoldOut !== 'true') {
      firstValid = input;
    }
  });

  // If the currently selected slot just became closed, move the selection to
  // the next valid one by simulating a real click, so the theme's own
  // variant-picker logic (price, add-to-cart button, cart properties) stays
  // in sync exactly as it would for a manual user selection.
  if (checkedIsClosed && firstValid && !firstValid.checked) {
    firstValid.click();
  }
}

function refreshAll() {
  document.querySelectorAll('variant-picker').forEach(refreshVariantPicker);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', refreshAll);
} else {
  refreshAll();
}

document.addEventListener(StandardEvents.productSelect, (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const picker = target.closest('variant-picker');
  if (!picker) return;

  const promise = event.promise;
  if (promise && typeof promise.then === 'function') {
    promise.then(() => refreshVariantPicker(picker)).catch(() => {});
  } else {
    refreshVariantPicker(picker);
  }
});

// Belt-and-braces: some theme flows re-render/re-sync the variant picker's
// markup (e.g. restoring a previously selected option) without dispatching a
// ProductSelectEvent. Watch the DOM directly so a closed slot never slips
// through, however the picker's content ends up changing.
let refreshQueued = false;
function queueRefresh() {
  if (refreshQueued) return;
  refreshQueued = true;
  setTimeout(() => {
    refreshQueued = false;
    refreshAll();
  }, 50);
}

const observer = new MutationObserver((mutations) => {
  const relevant = mutations.some((mutation) => {
    if (mutation.type === 'childList') return true;
    if (mutation.type === 'attributes') {
      // Ignore attribute changes we caused ourselves.
      return !['disabled', 'aria-disabled', 'data-option-available', 'hidden'].includes(mutation.attributeName ?? '');
    }
    return false;
  });
  if (relevant) queueRefresh();
});

function observeVariantPickers() {
  document.querySelectorAll('variant-picker').forEach((picker) => {
    observer.observe(picker, { childList: true, subtree: true, attributes: true });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', observeVariantPickers);
} else {
  observeVariantPickers();
}
